/**
 * App-facing aliases over the generated schema.
 *
 * `generated/schema.d.ts` is the only file allowed to be imported from outside
 * this directory's own modules; everything else in the app reaches the API types
 * through here, so a rename in the spec is absorbed in one place.
 */
import type { components } from './generated/schema'

type Schemas = components['schemas']

/* ------------------------------------------------------------------ fields */

/**
 * The polymorphic field union. drf-spectacular emits a `discriminator` on `type`
 * with a full 27-entry mapping, so `Extract<Field, { type: 'text' }>` narrows
 * without hand-written guards.
 */
export type Field = Schemas['FieldField']
export type FieldWithRelated = Schemas['FieldFieldSerializerWithRelatedFields']
export type FieldTypeName = Field['type']
export type FieldOf<T extends FieldTypeName> = Extract<Field, { type: T }>

/**
 * What a `PATCH` to a field may say, as the same discriminated union.
 *
 * A separate union from `Field` rather than a `Partial` of it, because the two
 * genuinely differ: `read_only`, `immutable_type` and the related fields are
 * things the API tells you and will not be told, and a formula field's
 * expression is *required* here while everything beside it is optional.
 *
 * Every property is optional and an omitted one is left alone, which is the
 * whole reason a field this build cannot configure can still be renamed.
 */
export type FieldUpdate = Schemas['PatchedFieldUpdateField']

/**
 * Two properties a formula field's *request* schema carries and no request may
 * set: whether the expression is in error, and whether it can produce nothing.
 * The backend declares them read-only rather than leaving them out, which
 * openapi-typescript renders as required-and-readonly -- so a body this
 * application builds would have to carry two answers only the server can give.
 */
type AnsweredNotAsked = 'error' | 'nullable'

export type FieldUpdateOf<T extends FieldTypeName> = Omit<
  Extract<FieldUpdate, { type: T }>,
  AnsweredNotAsked
>

/**
 * The properties that belong to a field *type* rather than to every field.
 *
 * The four subtracted here are asked for in the same dialog and travel in the
 * same request, but they are the same four whatever the type is -- so keeping
 * them out is what makes the rest a bag that can be handed to one subform and
 * carried back without either end naming a type.
 */
export type FieldPropertiesOf<T extends FieldTypeName> = Omit<
  FieldUpdateOf<T>,
  'type' | 'name' | 'description' | 'ai_description'
>

/**
 * One choice a select field offers.
 *
 * The same shape in both directions, which is unusual here and is what lets the
 * dialog hand back what it was given: `id` is absent on an option that does not
 * exist yet, and the colour is a *name* from a fixed palette rather than a
 * value -- see `features/database/fields/select-options.ts`.
 */
export type SelectOption = Schemas['SelectOption']

/* ------------------------------------------------------------------- views */

/**
 * The related table's primary field, on a link row field.
 *
 * Declared here rather than taken from the generated schema. The spec describes
 * it correctly -- a nullable `FieldField` -- but openapi-typescript renders a
 * `$ref` wrapped in `allOf` as `Omit<..., 'type'>`, assuming some parent supplies
 * the discriminator. Nothing does, and `type` is the property this value exists
 * to be read for: ordering a view by a link row means dispatching on the related
 * field's type. `test_link_row_primary_field_carries_its_type` holds the backend
 * to serving it.
 */
export type LinkRowPrimaryField = Field | null

/** The three date orderings and two clock formats a date field can choose. */
export type DateFormat = Schemas['DateFormatEnum']
export type TimeFormat = Schemas['DateTimeFormatEnum']

export type View = Schemas['ViewView']
export type ViewTypeName = View['type']
export type ViewOf<T extends ViewTypeName> = Extract<View, { type: T }>

export type ViewFilter = Schemas['ViewFilter']
export type ViewFilterGroup = Schemas['ViewFilterGroup']

/**
 * The 91 filter types the backend registers.
 *
 * Taken from the write serializers' enum rather than from `ViewFilter['type']`,
 * which the read serializer types as a plain `string`. The asymmetry is a
 * backend typing gap, not a real one -- a filter that comes back from the API
 * can only be a type the API accepted. Narrowing here means the filter map can
 * be declared `satisfies Record<ViewFilterTypeName, ...>` and a filter the
 * backend adds fails to compile until the frontend answers for it.
 *
 * Verified to agree exactly with the frontend's registrations: 91 on each side,
 * same names, no member on one side missing from the other.
 */
export type ViewFilterTypeName = Schemas['Type4e1Enum']
export type ViewSort = Schemas['ViewSort']
export type ViewGroupBy = Schemas['ViewGroupBy']
export type ViewDecoration = Schemas['ViewDecoration']

/** What a new row in a view starts with, per column. */
export type ViewDefaultValue = Schemas['ViewDefaultValue']

/**
 * A decoration as the decoration endpoints answer, discriminated on the value
 * provider.
 *
 * `ViewDecoration` is what a *view* carries, and it types
 * `value_provider_conf` as `unknown` -- one field holding two entirely
 * different shapes, which the read serializer does not describe. The decoration
 * endpoints do describe it, so anything editing a decoration works in this type
 * and only the reading side is left to hand-typing.
 */
export type DecorationOf<T extends ValueProviderTypeName> = Extract<
  Schemas['Decorator_Value_Provider_TypeViewDecoration'],
  { value_provider_type: T }
>
export type Decoration = Schemas['Decorator_Value_Provider_TypeViewDecoration']
export type DecorationCreate =
  Schemas['Decorator_Value_Provider_TypeCreateViewDecoration']

/**
 * What a `PATCH` to a decoration may say.
 *
 * Hand-written, because the generated union requires `value_provider_type` on
 * every member and the endpoint does not: it falls back to the provider the
 * decoration already has, so `{ type: 'background_color' }` on its own is a
 * request it accepts. drf-spectacular has no way to say that -- the mapping
 * between the discriminator and the shapes *is* how a polymorphic body is
 * described -- so the spec is stricter here than the endpoint rather than
 * wrong about it.
 *
 * What the union does keep is that a provider and its configuration travel
 * together, which is real: the endpoint validates the configuration against
 * whichever provider the request names.
 */
export type DecorationUpdate =
  | { type: DecoratorTypeName }
  | { value_provider_type: 'single_select_color'; value_provider_conf: SelectColorConf }
  | {
      value_provider_type: 'conditional_color'
      value_provider_conf: ConditionalColorConf
    }

/**
 * The body the spec declares for a decoration `PATCH`.
 *
 * Named only so the cast in `decoration-mutations.ts` has something to point
 * at; `DecorationUpdate` above is what anything editing a decoration builds.
 */
export type DecorationUpdateBody =
  Schemas['PatchedDecorator_Value_Provider_TypeUpdateViewDecoration']

/** Where a decoration puts its colour, and how the colour is worked out. */
export type DecoratorTypeName = Schemas['Type516Enum']
export type ValueProviderTypeName = Decoration['value_provider_type']

/** The two configurations, one per value provider. */
export type SelectColorConf = Schemas['SelectColorValueProviderConf']
export type ConditionalColorConf = Schemas['ConditionalColorValueProviderConfColors']
export type ConditionalColor = Schemas['ConditionalColorValueProviderConfColor']
export type ConditionalColorFilter =
  Schemas['ConditionalColorValueProviderConfColorFilter']
export type ConditionalColorFilterGroup =
  Schemas['ConditionalColorValueProviderConfColorFilterGroup']
export type ViewFieldOptions = Schemas['ViewFieldOptions']

/* ---------------------------------------------------------------- settings */

/**
 * `GET /api/settings/` — unauthenticated, and read before sign-in.
 *
 * `frontend` carries the backend-owned limits the UI mirrors. They live on the
 * backend rather than in the frontend's own environment so there is exactly one
 * place to set each of them; see `FrontendSettingsDataType`.
 */
export type Settings = Schemas['SettingsResponse']
export type FrontendSettings = Schemas['FrontendSettings']

/**
 * What an administrator may change, which is a *smaller* shape than what is
 * read.
 *
 * `PATCH /api/settings/update/` answers with `Settings` and not with
 * `SettingsResponse`: no `frontend` bucket. Writing that answer over the cached
 * settings would take the instance's limits away from every page that reads
 * them, so the answer is merged rather than stored.
 */
export type SettingsUpdate = Schemas['PatchedSettings']

/* ------------------------------------------------------------------- admin */

/** An account, as only an administrator sees it. */
export type AdminUser = Schemas['UserAdminResponse']

/** Whether the services this instance depends on are answering. */
export type HealthCheck = Schemas['FullHealthCheck']

/** What the instance is holding, reported beside the health checks. */
export type InstanceUsage = Schemas['InstanceUsage']
export type EmailTestResult = Schemas['EmailTesterResponse']
/** What the host, the database and the background workers are doing now. */
export type SystemMetrics = Schemas['SystemMetrics']

/* ------------------------------------------------------- core domain objects */

/** The signed-in user, as every response that carries one describes them. */
export type AuthenticatedUser = Schemas['AuthenticatedUser']
/** One permission manager's contribution to the global permission list. */
export type GlobalPermissions = Schemas['GlobalPermissions']
/** A response that renews the access token but hands out no refresh token. */
export type AccessTokenResponse = Schemas['AccessTokenResponse']

/** A webhook, the events it listens for, and one made-up call to try it. */
export type TableWebhook = Schemas['TableWebhook']
export type WebhookEvent = Schemas['EventsEnum']
export type WebhookCall = Schemas['TableWebhookCall']
export type WebhookTestCall = Schemas['TableWebhookTestCallResponse']

/** An export in progress, and the file it becomes. */
export type ExportJob = Schemas['ExportJob']
export type ExportCharset = Schemas['ExportCharsetEnum']
export type CsvColumnSeparator = Schemas['CsvColumnSeparatorEnum']

export type Application = Schemas['ApplicationApplication']
export type Table = Schemas['Table']

/**
 * A database token: a key that authorises row reads and writes over the API
 * without a session, owned by one account and scoped to one workspace.
 */
export type DatabaseToken = Schemas['Token']
export type DatabaseTokenPermissions = DatabaseToken['permissions']

/**
 * A workspace as an application refers to one: the identity and nothing else.
 *
 * Not what the workspace list returns -- see `UserWorkspace`, which is the type
 * almost everything in the interface actually wants.
 */
export type Workspace = Schemas['Workspace']

/**
 * A workspace as the signed-in user sees it: the members, their own position in
 * the list, their permissions in it, and their unread count.
 *
 * What `GET /api/workspaces/` returns, and what the `qk.workspaces()` cache
 * holds. The realtime layer patches that cache in place, so its element type is
 * this one rather than the bare `Workspace`.
 */
export type UserWorkspace = Schemas['WorkspaceUserWorkspace']

/**
 * The editable half of an account.
 *
 * `language` is on the serializer and deliberately unused: the language picker
 * and the 20 non-English locales are dropped. Onboarding and guided tours were
 * on it too until the backend stopped recording either.
 */
export type Account = Schemas['PatchedAccount']

/** How often the backend is allowed to email about notifications. */
export type EmailNotificationFrequency = Schemas['EmailNotificationFrequencyEnum']

/** One person's membership of one workspace. */
export type WorkspaceUser = Schemas['WorkspaceUser']

/**
 * A long-running server task the user can watch to completion.
 *
 * The polymorphic form rather than the base one: the list endpoint returns each
 * job as its own type, and the fields that say *what* a job is about -- which
 * application is being duplicated, which table imported -- live only there.
 * Discriminated on `type`, so narrowing is a comparison rather than a cast.
 */
export type Job = Schemas['Job_TypeJob']

/** Duplicating an application, the one job kind this build starts so far. */
export type DuplicateApplicationJob = Extract<Job, { type: 'duplicate_application' }>

/**
 * One notification as the list endpoint returns it, carrying this user's read
 * flag alongside the notification itself.
 *
 * Its `data` is deliberately untyped by the schema -- the backend puts a
 * different dataclass in it for each notification type and describes none of
 * them -- so the shapes are hand-written in `features/notification/types.ts`,
 * where they are narrowed once and only once.
 */
export type Notification = Schemas['NotificationRecipient']

/** Whoever caused a notification. Absent for the ones the system raises itself. */
export type NotificationSender = Schemas['Sender']

/** One thing in the trash, of whichever kind. */
export type TrashEntry = Schemas['TrashContents']

/** The workspaces and applications the trash can be browsed by. */
export type TrashStructure = Schemas['TrashStructure']

/* -------------------------------------------------------------------- rows */

/**
 * Rows are hand-typed on purpose. The backend synthesises a single example row
 * serializer describing a fictional table (`get_example_row_serializer_class`),
 * so the generated row types describe no real table and cannot be narrowed.
 *
 * Type safety is recovered at exactly one boundary — `readCell(row, field)` —
 * where the field's `type` selects the value type. Components receive typed cell
 * props and never index `field_123` themselves.
 */
export type FieldKey = `field_${number}`

export interface RowData {
  id: number
  /**
   * Arbitrary-precision decimal as a string. Never `parseFloat` it: ordering
   * uses steps as small as 1e-20 and the precision does not survive a double.
   */
  order: string
  [key: FieldKey]: unknown
}

export const fieldKey = (id: number): FieldKey => `field_${id}`

/**
 * One comment left on a row.
 *
 * `message` is the stored ProseMirror document and `message_text` is the
 * backend's own rendering of it, mentions included as `@Name`. This build reads
 * the text and never the document: rendering the document would be a second
 * implementation of that rendering, in a different language, kept in step by
 * hand.
 */
export type RowComment = Schemas['RowComment']

/**
 * One thing that has been done to a row.
 *
 * `before`, `after` and `fields_metadata` are `unknown` in the schema, for the
 * same reason a row is: they are keyed by `field_123` and hold whatever that
 * field type stores. They are narrowed once, in
 * `features/database/row/history.ts`.
 *
 * The author's name travels *on the entry* rather than being looked up, which
 * is the opposite of a comment: the row-history table denormalises it, so an
 * entry still says who made the change after that account has gone.
 */
export type RowHistory = Schemas['RowHistory']

/**
 * One entry in the database-wide activity history.
 *
 * The same entry as above with four things the row panel has no use for: which
 * table and row it belongs to, where the change came in through, and the two
 * ids a revert is addressed by -- the single operation (`action_uuid`) and the
 * batch it was part of (`action_group`), which for an agent is one MCP session.
 */
export type DatabaseRowHistory = Schemas['DatabaseRowHistory']

/** Every entry sharing one `action_uuid`, collapsed into one line. */
export type DatabaseRowHistoryGroup = Schemas['DatabaseRowHistoryGroup']

/** What a revert did, row by row. */
export type RevertResult = Schemas['RevertRowHistoryResult']

export type RevertOutcome = Schemas['RowRevertOutcome']

/**
 * A database application, which is the only kind that holds tables.
 *
 * Narrowed from the union rather than named separately, so a `tables` array is
 * only reachable once the type has been checked -- the sidebar, the API
 * reference and the link-row picker all have to make that check anyway.
 */
export type DatabaseApplication = Extract<Application, { type: 'database' }>

/* ---------------------------------------------------- workspace transfers */

/** One export of a workspace's applications, and the archive it produced. */
export type WorkspaceExport = Schemas['ExportApplicationsJobTypeResponse']

/**
 * What may be asked for when starting an export.
 *
 * The two subtracted are `AnsweredNotAsked` again, in a different place: the
 * job type composes its response mixin into the request serializer too, so the
 * generated body requires the archive's url and filename -- two things only the
 * server can know, and only once the job it has not started yet has finished.
 */
export type WorkspaceExportRequest = Omit<
  Schemas['ExportApplicationsJobTypeRequest'],
  'url' | 'exported_file_name'
>

/** An uploaded archive, waiting to be imported. */
export type ImportResource = Schemas['ImportResource']

/* ----------------------------------------------------- workspace settings */

/**
 * How a workspace has configured its Big Job Agent, plus two things about the
 * server: whether a connector gateway exists at all, and what it can run on.
 */
export type AgentSettings = Schemas['WorkspaceAgentSettingsResponse']

/** One MCP server a workspace lends the agent. */
export type AgentMcpServer = Schemas['AgentMCPServer']

/**
 * What may be sent back.
 *
 * `AnsweredNotAsked` once more: the request body reuses the same component as
 * the response, so it requires `has_token` -- which is the server reporting
 * whether it holds a token, and not something a request may claim.
 */
export type AgentSettingsRequest = Omit<
  Schemas['PatchedWorkspaceAgentSettings'],
  'mcp_servers'
> & {
  mcp_servers?: Omit<AgentMcpServer, 'has_token'>[]
}

/** One credential the connector gateway holds, offered as a choice. */
export type GatewayConnection = Schemas['GatewayConnection']
export type AgentModel = Schemas['AgentModel']

/**
 * Which row history a workspace records and how long it keeps it, alongside
 * the plan's ceilings the admin's choices sit under.
 */
export type RowHistorySettings = Schemas['RowHistorySettingsResponse']

/** What may be sent back: any of the admin's three choices. */
export type RowHistorySettingsRequest = Schemas['PatchedRowHistorySettings']

/** A kind of change that can be left unrecorded. */
export type RowHistoryAction = Schemas['DisabledActionsEnum']

/** A channel whose changes can be left unrecorded. */
export type RowHistorySource = Schemas['DisabledSourcesEnum']

/* ---------------------------------------------------------- MCP settings */

/** Where the MCP export tools stream their files, which the instance decides. */
export type McpExportSettings = Schemas['MCPExportSettings']

/* -------------------------------------------------------------- data sync */

/**
 * A table kept in step with something outside this instance.
 *
 * The union is discriminated on `type`, which is what lets a form be built from
 * a map that has to answer for every member.
 */
export type DataSync = Schemas['Data_SyncDataSync']
export type DataSyncTypeName = Schemas['PatchedData_SyncUpdateDataSync']['type']
export type DataSyncOf<T extends DataSyncTypeName> = Extract<DataSync, { type: T }>

/** One thing a source offers, which may or may not be synced into a column. */
export type DataSyncProperty = Schemas['ListDataSyncProperty']

/**
 * Which workspace members may open a table, when it is restricted at all, and
 * whether AI connections may reach it.
 */
export type TableAccess = Schemas['TableAccess']

/**
 * Which workspace members may open a database or dashboard, when it is
 * restricted at all, and whether AI connections may reach it.
 */
export type ApplicationAccess = Schemas['ApplicationAccess']

/**
 * How one column is governed: the members its values are hidden from, and
 * whether AI connections may read it.
 */
export type FieldRedaction = Schemas['FieldRedaction']

/* --------------------------------------------------------------- snapshots */

/** One backup of an application, taken by hand or by its schedule. */
export type Snapshot = Schemas['Snapshot']

/**
 * When an application backs itself up.
 *
 * Every time value on it is **UTC**, which the model says plainly and the
 * frontend is expected to convert -- see `features/snapshot/schedule.ts`.
 */
export type SnapshotSchedule = Schemas['SnapshotSchedule']

/** Where a snapshot is kept: inside the database, or an archive in a bucket. */
export type SnapshotDestination = Schemas['SnapshotDestination']

/**
 * A dashboard application.
 *
 * The only application type with anything of its own on the list endpoint: a
 * `description`, which the dashboard shows under its name.
 */
export type Dashboard = Extract<Application, { type: 'dashboard' }>

/**
 * One thing shown on a dashboard.
 *
 * Two types, and both of them hold a `data_source_id` rather than a query:
 * creating a widget creates its data source server-side, and deleting the
 * widget deletes it again. So a widget is never without one and nobody ever
 * chooses which kind it is.
 */
export type Widget = Schemas['Dashboard_WidgetWidget']
export type WidgetTypeName = Widget['type']
export type WidgetOf<T extends WidgetTypeName> = Extract<Widget, { type: T }>
export type WidgetCreate = Schemas['Dashboard_WidgetCreateWidget']
export type WidgetUpdate = Schemas['PatchedDashboard_WidgetUpdateWidget']

/** The shapes a chart widget can take. */
export type ChartType = Schemas['ChartTypeEnum']

/**
 * Where a widget's numbers come from.
 *
 * The schema enumerates every service type in the application, because a data
 * source is a link to a `Service` and the serializer is generated over the whole
 * registry -- twenty-one of them. Only two can be reached: the summary widget's
 * is always an aggregation over a table, and the chart widget's is always the
 * same aggregation grouped by a field. The rest answered to the builder and the
 * automation until M8 removed both, and are now unreachable from anywhere;
 * nothing in this application can put one on a dashboard.
 */
export type DashboardDataSource = Schemas['Integration_ServiceDashboardDataSource']
export type SummaryDataSource = Extract<
  DashboardDataSource,
  { type: 'local_baserow_aggregate_rows' }
>
export type ChartDataSource = Extract<
  DashboardDataSource,
  { type: 'local_baserow_category_aggregate_rows' }
>
export type DashboardDataSourceUpdate =
  Schemas['PatchedIntegration_ServiceUpdateDashboardDataSource']

/**
 * What a data source is allowed to read.
 *
 * An integration is the authorised connection a dashboard reads its tables
 * through, and its `context_data` is the whole catalogue: every database the
 * integration's user can see, the tables in each, and the views of those tables
 * that can filter or sort. It is the only list the configuration forms need --
 * asking the workspace for its applications would answer with what the *reader*
 * can see, which is a different question.
 */
export type Integration = Schemas['IntegrationIntegration']
export type LocalBaserowIntegration = Extract<Integration, { type: 'local_baserow' }>
export type IntegrationDatabase = Schemas['LocalBaserowDatabaseApplication']
export type IntegrationTable = Schemas['LocalBaserowTable']
export type IntegrationView = Schemas['LocalBaserowView']

/* --------------------------------------------------------------- templates */

/**
 * A template as the gallery lists it: a platform template (its id is a slug) or
 * one of this workspace's company templates (its id is a number, as a string).
 */
export type TemplateListItem = Schemas['TemplateListItem']
export type TemplateList = Schemas['TemplateList']
export type TemplateCategory = Schemas['TemplateCategory']
export type TemplateCategoryId = Schemas['TemplateCategoriesEnum']
export type TemplateCounts = Schemas['TemplateCounts']
export type TemplateInstallMode = Schemas['TemplateInstallModeEnum']

/** One template in full: what it holds, and the items it was made from. */
export type Template = Schemas['Template']
export type TemplateItem = Schemas['TemplateItem']
export type TemplateSummary = Schemas['TemplateSummary']
export type TemplateDatabaseSummary = Schemas['TemplateDatabaseSummary']
export type TemplateTableSummary = Schemas['TemplateTableSummary']
export type TemplateDashboardSummary = Schemas['TemplateDashboardSummary']

/**
 * An application a template can be made from, and for a dashboard, the databases
 * it reads, which a template carries alongside it.
 */
export type TemplateCandidate = Schemas['TemplateCandidate']

export type TemplateCreate = Schemas['CreateTemplate']
export type TemplateUpdate = Schemas['PatchedUpdateTemplate']
